import { Injectable } from '@angular/core';
import { isDefined } from '@fi-shared/utils';
import {
    ITimePickerTimeLimits,
    ITimeSegment,
    IAvailableTimeSegments,
    ITimeSegmentInputData,
} from './fi-time-picker-input.interfaces';
import { fiFormatTwoDigit } from '@fi-shared/utils/fi-format-two-digits';
import { isNullOrUndefined } from '../utils/is-null-or-undefined';

export enum TimePickerSegmentIndex {
    Hours,
    Minutes,
    Seconds,
}

@Injectable()
export class FiTimePickerHelperService {
    readonly inputHighlightTimeout: number = 3000;

    handleNaNTimeSegment(value: string): string {
        if (!value.includes('NaN')) return value;

        return value
            .split(':')
            .map((segment: string, i: number) => {
                return isNaN(+segment) ? '' /* this.getTimeSegmentMinValue(i) */ : segment;
            })
            .join(':');
    }

    handleTimeSegmentSelect(args: IHandleTimeSegmentsSelectArgs): string {
        let timeSegments: string[] = args.currentValue?.split(':') || [];
        const minTimeSegments: number = this.getMinTimeSegments(args.showSeconds);
        if (timeSegments.length < minTimeSegments) {
            for (let i = 0; i < minTimeSegments; i++) {
                if (!timeSegments[i]?.length) {
                    timeSegments[i] = undefined;
                }
            }
        }

        timeSegments[args.segmentIndex] = args.newValue;

        if (timeSegments.some((segment: string) => !isDefined(segment))) {
            timeSegments = timeSegments.map((segment: string, i: number) => {
                return !isDefined(segment) ? args.getTimeSegmentMinValueFn(i) : segment;
            });
        }

        return timeSegments.join(':');
    }

    getMinTimeSegments(showSeconds: boolean): number {
        return showSeconds ? 3 : 2;
    }

    getInputMask(showSeconds: boolean): string {
        return showSeconds ? this.hourMinuteSecondsInputMask : this.hourMinuteInputMask;
    }

    getTimeSegment(arrLength: number, valFn?: (index: number) => number): string[] {
        return new Array<string>(arrLength)
            .fill('')
            .map((_: string, hourIndex: number) => fiFormatTwoDigit(isDefined(valFn) ? valFn(hourIndex) : hourIndex));
    }

    isHourDisabled(hour: number, selectedMinute: number, timeLimits: ITimePickerTimeLimits): boolean {
        if (hour > 23) return false;
        if (hour < timeLimits.minHour || hour > timeLimits.maxHour) return true;

        if (selectedMinute)
            return (
                (hour === timeLimits.minHour && selectedMinute < timeLimits.minMinute) ||
                (hour === timeLimits.maxHour && selectedMinute > timeLimits.maxMinute)
            );
    }

    isMinuteDisabled(minute: number, selectedHour: number, timeLimits: ITimePickerTimeLimits): boolean {
        if (isNullOrUndefined(selectedHour)) return false;

        return (
            (selectedHour === timeLimits.minHour && minute < timeLimits.minMinute) ||
            (selectedHour === timeLimits.maxHour && minute > timeLimits.maxMinute)
        );
    }

    getCurrentTimeLimits(args: IGetCurrentTimeLimitsArgs): ITimePickerTimeLimits {
        const minTime: string = (args.isMainInput ? args.minMainTime : args.minSecondaryTime) || '00:00';
        const maxTime: string = (args.isMainInput ? args.maxMainTime : args.maxSecondaryTime) || '23:59';
        const { hour: minHour, minute: minMinute } = this.extractHourAndMinute(minTime?.length ? minTime : '00:00');
        const { hour: maxHour, minute: maxMinute } = this.extractHourAndMinute(maxTime?.length ? maxTime : '23:59');
        return { minHour, maxHour, minMinute, maxMinute };
    }

    extractHourAndMinute(time: string): { hour: number; minute: number } {
        // todo: implement seconds handler;
        const [timeHour, timeMinute]: string[] = time.split(':');
        return { hour: +timeHour, minute: +timeMinute };
    }

    isTimeValueInvalid(args: IIsTimeValueInvalidArgs): boolean {
        const timeSegments: string[] = args.timeValue?.split(':') || [];

        if (!args.timeValue?.length || timeSegments.length < this.getMinTimeSegments(args.showSeconds)) {
            return true;
        }

        // const selectedTime = this.extractHourAndMinute(args.currentTimeValue);
        // const selectedHour: string = fiFormatTwoDigit(selectedTime.hour);
        // const selectedMinute: string = fiFormatTwoDigit(selectedTime.minute);
        // const isSelectedTimeDisabled =
        //     args.isTimeSegmentDisabledFn(selectedHour, TimePickerSegmentIndex.Hours) ||
        //     args.isTimeSegmentDisabledFn(selectedMinute, TimePickerSegmentIndex.Minutes);

        return (
            // isSelectedTimeDisabled ||
            timeSegments.some((timeSegment: string) => !timeSegment?.length) ||
            +timeSegments[TimePickerSegmentIndex.Hours] >= 24 ||
            +timeSegments[TimePickerSegmentIndex.Minutes] >= 60 ||
            (args.showSeconds && +timeSegments[TimePickerSegmentIndex.Minutes] >= 60)
        );
    }
    isTimeSegmentDisabled(
        timeLimits: ITimePickerTimeLimits,
        segmentIndex: TimePickerSegmentIndex,
        timeSegment: ITimeSegment,
        currentSegmentInputData: ITimeSegmentInputData,
    ): boolean {
        if (segmentIndex === TimePickerSegmentIndex.Hours) {
            return this.isHourDisabled(timeSegment.id, +currentSegmentInputData.minutes?.value, timeLimits);
        }

        if (segmentIndex === TimePickerSegmentIndex.Minutes) {
            return this.isMinuteDisabled(+timeSegment.value, +currentSegmentInputData.hours?.value, timeLimits);
        }

        return false;
    }

    convertStringToTimeSegmentInputData(
        inputValue: string,
        segments: IAvailableTimeSegments,
        isOvertime: boolean,
    ): ITimeSegmentInputData {
        if (!isDefined(inputValue) || inputValue === '') {
            return {
                hours: { id: undefined, value: undefined },
                minutes: { id: undefined, value: undefined },
                seconds: { id: undefined, value: undefined },
            };
        }
        const inputValueTimeSegments = inputValue.split(':');

        const hoursValue = inputValueTimeSegments[0];
        const minutesValue = inputValueTimeSegments[1];
        const secondsValue = inputValueTimeSegments[2];

        let hoursId = undefined;
        if (isOvertime) {
            hoursId = segments.hours.find((timeSegment) => timeSegment.value === hoursValue && timeSegment.id > 23)?.id;
        } else {
            hoursId = segments.hours.find((timeSegment) => timeSegment.value === hoursValue)?.id;
        }

        return {
            hours: {
                id: hoursId,
                value: hoursValue,
            },
            minutes: {
                id: segments.minutes.find((timeSegment) => timeSegment.value === minutesValue)?.id,
                value: minutesValue,
            },
            seconds: {
                id: segments.seconds.find((timeSegment) => timeSegment.value === secondsValue)?.id,
                value: secondsValue,
            },
        };
    }

    convertTimeSegmentInputDataToString(inputData: ITimeSegmentInputData, showSeconds: boolean): string {
        if (!isDefined(inputData)) {
            return '';
        }
        let inputValue = '';
        let hoursValue = inputData.hours?.value;
        let minutesValue = inputData.minutes?.value;

        if (hoursValue) {
            inputValue += hoursValue;
        }
        if (minutesValue) {
            inputValue += `:${minutesValue}`;
        }

        const secondsValue = inputData.seconds?.value;
        if (showSeconds && secondsValue) {
            inputValue += `:${secondsValue}`;
        }

        return inputValue;
    }

    private readonly hourMinuteInputMask: string = 'hh:mm';
    private readonly hourMinuteSecondsInputMask: string = 'hh:mm:ss';
}

interface IHandleTimeSegmentsSelectArgs {
    currentValue: string;
    newValue: string;
    segmentIndex: number;
    showSeconds: boolean;
    getTimeSegmentMinValueFn: (number) => string;
}

interface IGetCurrentTimeLimitsArgs {
    isMainInput: boolean;
    minMainTime: string;
    maxMainTime: string;
    minSecondaryTime: string;
    maxSecondaryTime: string;
}

interface IIsTimeValueInvalidArgs {
    timeValue: string;
    currentTimeValue: string;
    showSeconds: boolean;
}
